import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";

/**
 * MusicPlayerContext — owns the global <audio> element so playback survives
 * leaving the Music tab (V2.3.9). MusicTab and any future widget read/write
 * `track`, `playing`, `volume`, `source`, etc. through this context.
 */
const Ctx = createContext(null);
export const useMusicPlayer = () => useContext(Ctx);

const PRESET_TRACKS = [
  { id: "p-rock",    name: "Rock",        url: "/music/preset/rock.mp3" },
  { id: "p-country", name: "Country",     url: "/music/preset/country.mp3" },
  { id: "p-jazz",    name: "Jazz",        url: "/music/preset/jazz.mp3" },
  { id: "p-kpop",    name: "K-Pop",       url: "/music/preset/k-pop.mp3" },
  { id: "p-soul",    name: "Soul / R&B",  url: "/music/preset/soul-rnb.mp3" },
];

export function MusicPlayerProvider({ children }) {
  const [uploads, setUploads] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ict.music.uploads")) || []; } catch { return []; }
  });
  const [source, setSource] = useState(() => localStorage.getItem("ict.music.source") || "preset");
  const [trackIdx, setTrackIdx] = useState(() => {
    const v = parseInt(localStorage.getItem("ict.music.trackIdx")); return Number.isFinite(v) ? v : 0;
  });
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(() => {
    const v = parseInt(localStorage.getItem("ict.music.volume")); return Number.isFinite(v) ? v : 30;
  });
  const audioRef = useRef(null);

  const tracks = source === "uploads" ? uploads : PRESET_TRACKS;
  const current = tracks[trackIdx] || null;

  // Persist
  useEffect(() => { try { localStorage.setItem("ict.music.uploads", JSON.stringify(uploads)); } catch {} }, [uploads]);
  useEffect(() => { localStorage.setItem("ict.music.volume", String(volume)); }, [volume]);
  useEffect(() => { localStorage.setItem("ict.music.source", source); }, [source]);
  useEffect(() => { localStorage.setItem("ict.music.trackIdx", String(trackIdx)); }, [trackIdx]);

  // Volume sync
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = Math.max(0, Math.min(1, volume / 100));
  }, [volume]);

  // V2.9.5 — Skip first-mount-source effect so restored state isn't clobbered.
  const firstSourceChange = useRef(true);
  useEffect(() => {
    if (firstSourceChange.current) { firstSourceChange.current = false; return; }
    setTrackIdx(0);
    setPlaying(false);
  }, [source]);

  // V2.9.5 — Imperative audio src management: only swap src when track URL actually changes,
  // so navigation re-renders of the provider don't reload (and reset) the audio element.
  const lastSrcRef = useRef("");
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const desired = current?.url || "";
    if (desired && desired !== lastSrcRef.current) {
      a.src = desired;
      lastSrcRef.current = desired;
      a.load();
    }
    if (playing && desired) { a.play().catch(() => setPlaying(false)); }
    else if (!playing) { a.pause(); }
  }, [playing, current?.url]);

  const togglePlay = useCallback(() => setPlaying(p => !p), []);
  const next = useCallback(() => setTrackIdx(i => (i + 1) % Math.max(1, tracks.length)), [tracks.length]);
  const prev = useCallback(() => setTrackIdx(i => (i - 1 + tracks.length) % Math.max(1, tracks.length)), [tracks.length]);

  return (
    <Ctx.Provider value={{
      PRESET_TRACKS,
      source, setSource,
      uploads, setUploads,
      tracks, current,
      trackIdx, setTrackIdx,
      playing, setPlaying, togglePlay,
      volume, setVolume,
      next, prev,
    }}>
      {/* Global hidden audio element. Survives tab/route changes. */}
      {/* V2.9.5 — no `src=""` declarative attribute; we set it imperatively. */}
      <audio
        ref={audioRef}
        onEnded={next}
        preload="none"
        data-testid="global-audio"
      />
      {children}
    </Ctx.Provider>
  );
}
